import React, { useContext, useEffect } from "react";
import { Stack, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { custom_context } from "../../../utils/custom_context";
import StatusTag from "../../../components/Common/StatusTag";
import { STATUS_MAP } from "../../../utils/constant";

import "../dashboard_styles.css";

/**
 * Maps disbursal backend statuses
 * to existing STATUS_MAP entries
 */
const mapDisbursalStatusToUiStatus = (disbursalStatus) => {
  switch (disbursalStatus) {
    case "initiated":
      return STATUS_MAP.initiated;

    case "approved":
      return STATUS_MAP.approved;

    case "success":
      return STATUS_MAP.completed;

    case "rejected":
      return STATUS_MAP.rejected;

    case "failed":
      return STATUS_MAP.failed;

    default:
      return STATUS_MAP.pending;
  }
};

function DisbursalStatus() {
  const { applicationData } = useContext(custom_context.ApplicationData);
  const navigate = useNavigate();

  const loan = applicationData?.loan;
  const disbursal = applicationData?.disbursal;

  const disbursalStatus = disbursal?.status;

  const isRevenue = loan?.type_of_financing === "revenue";
  const isInvoice = loan?.type_of_financing === "invoice";

  const lmsDetails = loan?.lms_integration_details;

  const isLmsReady =
    !!lmsDetails?.program_mapping_uuid &&
    !!lmsDetails?.company_uuid;

  /**
   * Account setup condition (common for BOTH invoice & revenue)
   */
  const isAccountSetupDone =
    applicationData?.virtual_account?.status === "approved" &&
    applicationData?.evaluation?.status === "approved";

  /**
   * Eligibility condition (Invoice requires LMS integration as well)
   */
  const isInvoiceReady =
    isAccountSetupDone &&
    isLmsReady;

  const uiStatus = mapDisbursalStatusToUiStatus(disbursalStatus);

  /**
   * Redirection Logic
   */
  useEffect(() => {
    if (!loan) return;

    // Case 1: Invoice loan + full eligibility → redirect to invoice module
    if (isInvoice && isInvoiceReady) {
      navigate(`/invoice/${loan.uuid}`, { replace: true });
      return;
    }

    // Case 2: Revenue + disbursal success + account setup done → redirect to LMS dashboard
    if (isRevenue && isAccountSetupDone && disbursalStatus === "success") {
      navigate(`/lms/${loan.uuid}/dashboard`, { replace: true });
      return;
    }
  }, [
    loan,
    isInvoice,
    isRevenue,
    isInvoiceReady,
    isAccountSetupDone,
    disbursalStatus,
    navigate,
  ]);

  /**
   * Case 3:
   *
   * Render card when:
   * - Invoice or Revenue loan exists
   * - Before readiness / success
   *
   * Frozen state → before account setup
   * Active state → after setup while processing
   */

  const shouldRenderCard =
    (isInvoice || isRevenue) &&
    (
      !isAccountSetupDone ||
      (isRevenue && disbursalStatus !== "success") ||
      (isInvoice && !isInvoiceReady)
    );

  if (!shouldRenderCard) return null;

  // Card state
  const disableCard = !isAccountSetupDone;

  // ---------- INVOICE CARD ----------
  if (isInvoice) {
    return (
      <Card
        className={`dashboardActionCard ${disableCard ? "disabledCard" : "activeCard"
          }`}
      >
        <Stack gap={2} className="actionContent">
          <h4 className="actionCardTitle h5 h-md4 d-flex align-items-center gap-2">
            Invoice Upload

            {/* Show status ONLY when active */}
            {!disableCard && (
              <StatusTag
                status={STATUS_MAP.pending.value}
                stepComplete={false}
                label={STATUS_MAP.pending.label}
              />
            )}
          </h4>

          <p className="actionCardDescription">
            {disableCard
              ? "Complete account setup to start uploading invoices."
              : "We are preparing your financing account. This may take a short time. Please check back later to start uploading invoices."}
          </p>
        </Stack>
      </Card>
    );
  }

  // ---------- REVENUE CARD ----------
  return (
    <Card
      className={`dashboardActionCard ${disableCard ? "disabledCard" : "activeCard"
        }`}
    >
      <Stack gap={2} className="actionContent">
        <h4 className="actionCardTitle h5 h-md4 d-flex align-items-center gap-2">
          {disableCard ? "Disbursal" : "Disbursal In Progress"}

          {/* Show status ONLY when active */}
          {!disableCard && (
            <StatusTag
              status={uiStatus.value}
              stepComplete={false}
              label={uiStatus.label}
            />
          )}
        </h4>

        <p className="actionCardDescription">
          {disableCard
            ? "Complete account setup to proceed with disbursal."
            : "Your loan will be created shortly. Please log in after sometime to view your loan details."}
        </p>
      </Stack>
    </Card>
  );
}

export default DisbursalStatus;
